// Debug logging - only enabled in development
const DEBUG = import.meta.env.DEV;

/**
 * MemeLaunch DBC Fee Metrics Client
 * 
 * This module wraps Meteora DBC SDK functions for:
 * - Fetching pool fee metrics (claimable partner/creator fees)
 * - Claiming partner trading fees to the treasury
 * - Claiming creator trading fees
 * 
 * All fee amounts are kept as BN (big numbers) until UI layer conversion.
 */

import * as Sentry from '@sentry/react';
import { Connection, PublicKey, Transaction, TransactionInstruction, SystemProgram, LAMPORTS_PER_SOL } from '@solana/web3.js';
import {
  getAssociatedTokenAddress,
  createAssociatedTokenAccountIdempotentInstruction,
  createCloseAccountInstruction,
  TOKEN_PROGRAM_ID,
  NATIVE_MINT,
} from '@solana/spl-token';
import BN from 'bn.js';
import {
  DynamicBondingCurveClient,
  createDammV2Program,
  derivePositionNftAccount,
} from '@meteora-ag/dynamic-bonding-curve-sdk';
import { MEMELAUNCH_TREASURY_PUBKEY } from './feeConfig';
import { MEMELAUNCH_DBC_CONFIG_KEY } from './dbcClient';
import { getSolanaWsEndpoint } from '@/lib/solana/config';

// ============================================================================
// TYPES
// ============================================================================

export interface PoolFeeMetrics {
  poolAddress: string;
  configKey: string;
  baseMint: string;
  quoteMint: string;
  creator: string;
  
  // Claimable fees (in lamports for SOL quote)
  partnerBaseFee: BN;
  partnerQuoteFee: BN;
  creatorBaseFee: BN;
  creatorQuoteFee: BN;
  
  // Protocol fees
  protocolBaseFee: BN;
  protocolQuoteFee: BN;
  
  // Migration status
  isMigrated: boolean;
  migrationProgress: number;
  
  // Fetch timestamp
  fetchedAt: Date;
  
  // Config validation
  feeClaimerMatchesTreasury: boolean;
  expectedFeeClaimer: string;
  actualFeeClaimer: string | null;
}

export interface ClaimFeeResult {
  success: boolean;
  txSignature?: string;
  error?: string;
  claimedBaseAmount?: BN;
  claimedQuoteAmount?: BN;
}

// ============================================================================
// ADMIN WALLET LIST
// ============================================================================

/**
 * Build the list of admin wallet addresses that can access the fee admin panel.
 * Reads from VITE_ADMIN_WALLETS env var (comma-separated) and always includes treasury.
 */
function buildAdminWalletList(): string[] {
  const envWallets = import.meta.env.VITE_ADMIN_WALLETS;
  const walletSet = new Set<string>();
  
  // Always include treasury
  walletSet.add(MEMELAUNCH_TREASURY_PUBKEY.toBase58());
  
  // Add wallets from env var
  if (envWallets && typeof envWallets === 'string') {
    envWallets.split(',').forEach((addr: string) => {
      const trimmed = addr.trim();
      if (trimmed.length > 0) {
        walletSet.add(trimmed);
      }
    });
  }
  
  if (!envWallets) {
    console.warn('[feeMetricsClient] VITE_ADMIN_WALLETS not set. Only treasury wallet has admin access.')
  }

  return Array.from(walletSet);
}

const ADMIN_WALLETS: string[] = buildAdminWalletList();

/**
 * Check if a wallet address is an admin
 */
export function isAdminWallet(walletAddress: string | null | undefined): boolean {
  if (!walletAddress) return false;
  return ADMIN_WALLETS.includes(walletAddress);
}

/**
 * Get the list of admin wallets
 */
export function getAdminWallets(): string[] {
  return [...ADMIN_WALLETS];
}

// ============================================================================
// MODULE-LEVEL CONFIG CACHE
// ============================================================================

// Pool configs are immutable on-chain. Cache them across all FeeMetricsClient
// instances to avoid redundant RPC calls (all dextoro pools share the same config).
const poolConfigCache = new Map<string, { feeClaimer: string; fetchedAt: number }>();
const CONFIG_CACHE_TTL = 30 * 60 * 1000; // 30 minutes

function getCachedConfig(configKey: string): string | null {
  const cached = poolConfigCache.get(configKey);
  if (cached && Date.now() - cached.fetchedAt < CONFIG_CACHE_TTL) {
    return cached.feeClaimer;
  }
  return null;
}

function setCachedConfig(configKey: string, feeClaimer: string): void {
  poolConfigCache.set(configKey, { feeClaimer, fetchedAt: Date.now() });
}

// ============================================================================
// FEE METRICS CLIENT
// ============================================================================

export class FeeMetricsClient {
  private connection: Connection;
  private dbcClient: DynamicBondingCurveClient;

  constructor(endpoint: string) {
    const wsEndpoint = getSolanaWsEndpoint();
    this.connection = new Connection(endpoint, {
      commitment: 'confirmed',
      wsEndpoint: wsEndpoint,
    });
    this.dbcClient = new DynamicBondingCurveClient(this.connection, 'confirmed');
    DEBUG && console.log('[FeeMetricsClient] Initialized with HTTP:', endpoint, 'WS:', wsEndpoint);
  }

  /**
   * Fetch fee metrics for a single pool
   */
  async fetchPoolFeeMetrics(poolAddress: PublicKey): Promise<PoolFeeMetrics | null> {
    DEBUG && console.log('[FeeMetricsClient] Fetching metrics for pool:', poolAddress.toBase58());
    
    try {
      // Get pool state
      const poolState = await this.dbcClient.state.getPool(poolAddress);
      
      if (!poolState) {
        console.warn('[FeeMetricsClient] Pool not found:', poolAddress.toBase58());
        return null;
      }

      // Get config to check feeClaimer (cached, configs are immutable on-chain)
      const configKey = poolState.config;
      const configKeyStr = configKey.toBase58();
      let actualFeeClaimer: string | null = getCachedConfig(configKeyStr);
      let feeClaimerMatchesTreasury = false;

      if (actualFeeClaimer) {
        feeClaimerMatchesTreasury = actualFeeClaimer === MEMELAUNCH_TREASURY_PUBKEY.toBase58();
      } else {
        try {
          const configState = await this.dbcClient.state.getPoolConfig(configKey);
          if (configState && configState.feeClaimer) {
            actualFeeClaimer = configState.feeClaimer.toBase58();
            feeClaimerMatchesTreasury = actualFeeClaimer === MEMELAUNCH_TREASURY_PUBKEY.toBase58();
            setCachedConfig(configKeyStr, actualFeeClaimer);
          }
        } catch (e) {
          console.warn('[FeeMetricsClient] Could not fetch config:', e);
        }
      }

      // Extract fee data from pool state
      const metrics: PoolFeeMetrics = {
        poolAddress: poolAddress.toBase58(),
        configKey: configKey.toBase58(),
        baseMint: poolState.baseMint.toBase58(),
        quoteMint: configKey.toBase58(), // Use config as proxy since pool doesn't have quoteMint directly
        creator: poolState.creator.toBase58(),
        
        // Partner fees (claimable by feeClaimer/treasury)
        partnerBaseFee: new BN(poolState.partnerBaseFee?.toString() || '0'),
        partnerQuoteFee: new BN(poolState.partnerQuoteFee?.toString() || '0'),
        
        // Creator fees (claimable by pool creator)
        creatorBaseFee: new BN(poolState.creatorBaseFee?.toString() || '0'),
        creatorQuoteFee: new BN(poolState.creatorQuoteFee?.toString() || '0'),
        
        // Protocol fees
        protocolBaseFee: new BN(poolState.protocolBaseFee?.toString() || '0'),
        protocolQuoteFee: new BN(poolState.protocolQuoteFee?.toString() || '0'),
        
        // Migration status
        isMigrated: poolState.isMigrated === 1 || poolState.isMigrated === true,
        migrationProgress: poolState.migrationProgress || 0,
        
        fetchedAt: new Date(),
        feeClaimerMatchesTreasury,
        expectedFeeClaimer: MEMELAUNCH_TREASURY_PUBKEY.toBase58(),
        actualFeeClaimer,
      };

      DEBUG && console.log('[FeeMetricsClient] Metrics fetched:', {
        pool: poolAddress.toBase58(),
        partnerQuoteFee: metrics.partnerQuoteFee.toString(),
        creatorQuoteFee: metrics.creatorQuoteFee.toString(),
        feeClaimerMatch: feeClaimerMatchesTreasury,
      });

      return metrics;
    } catch (error) {
      console.error('[FeeMetricsClient] Error fetching metrics:', error);
      return null;
    }
  }

  /**
   * Fetch fee metrics for multiple pools with concurrency control
   */
  async fetchBatchPoolFeeMetrics(
    poolAddresses: PublicKey[],
    concurrency: number = 3
  ): Promise<Map<string, PoolFeeMetrics | null>> {
    DEBUG && console.log('[FeeMetricsClient] Fetching batch metrics for', poolAddresses.length, 'pools');
    
    const results = new Map<string, PoolFeeMetrics | null>();
    
    // Process in batches
    for (let i = 0; i < poolAddresses.length; i += concurrency) {
      const batch = poolAddresses.slice(i, i + concurrency);
      const batchPromises = batch.map(async (addr) => {
        const metrics = await this.fetchPoolFeeMetrics(addr);
        return { address: addr.toBase58(), metrics };
      });
      
      const batchResults = await Promise.all(batchPromises);
      batchResults.forEach(({ address, metrics }) => {
        results.set(address, metrics);
      });
    }
    
    return results;
  }

  /**
   * Fetch fee metrics for all pools in a single RPC call using getMultipleAccountsInfo.
   * Much faster than per-pool fetches: 1 RPC call instead of N.
   */
  async fetchBatchPoolFeeMetricsFast(
    poolAddresses: PublicKey[]
  ): Promise<Map<string, PoolFeeMetrics | null>> {
    const results = new Map<string, PoolFeeMetrics | null>();
    if (poolAddresses.length === 0) return results;

    DEBUG && console.log('[FeeMetricsClient] Fast batch fetch for', poolAddresses.length, 'pools');

    try {
      // Single RPC call for all pool accounts (max 100 per call)
      const MAX_ACCOUNTS_PER_CALL = 100;
      const allAccountInfos: (import('@solana/web3.js').AccountInfo<Buffer> | null)[] = [];

      for (let i = 0; i < poolAddresses.length; i += MAX_ACCOUNTS_PER_CALL) {
        const batch = poolAddresses.slice(i, i + MAX_ACCOUNTS_PER_CALL);
        const infos = await this.connection.getMultipleAccountsInfo(batch);
        allAccountInfos.push(...infos);
      }

      const program = this.dbcClient.state.getProgram();

      for (let i = 0; i < poolAddresses.length; i++) {
        const accountInfo = allAccountInfos[i];
        const addr = poolAddresses[i].toBase58();

        if (!accountInfo?.data) {
          results.set(addr, null);
          continue;
        }

        try {
          const poolState = program.coder.accounts.decode('virtualPool', accountInfo.data);

          // Config validation (use module-level cache)
          const configKeyStr = poolState.config.toBase58();
          let actualFeeClaimer: string | null = getCachedConfig(configKeyStr);
          let feeClaimerMatchesTreasury = false;

          if (actualFeeClaimer) {
            feeClaimerMatchesTreasury = actualFeeClaimer === MEMELAUNCH_TREASURY_PUBKEY.toBase58();
          } else {
            // Fetch config only once per unique config key
            try {
              const configState = await this.dbcClient.state.getPoolConfig(poolState.config);
              if (configState && configState.feeClaimer) {
                actualFeeClaimer = configState.feeClaimer.toBase58();
                feeClaimerMatchesTreasury = actualFeeClaimer === MEMELAUNCH_TREASURY_PUBKEY.toBase58();
                setCachedConfig(configKeyStr, actualFeeClaimer);
              }
            } catch (e) {
              console.warn('[FeeMetricsClient] Could not fetch config in batch:', e);
            }
          }

          const metrics: PoolFeeMetrics = {
            poolAddress: addr,
            configKey: configKeyStr,
            baseMint: poolState.baseMint.toBase58(),
            quoteMint: configKeyStr,
            creator: poolState.creator.toBase58(),
            partnerBaseFee: new BN(poolState.partnerBaseFee?.toString() || '0'),
            partnerQuoteFee: new BN(poolState.partnerQuoteFee?.toString() || '0'),
            creatorBaseFee: new BN(poolState.creatorBaseFee?.toString() || '0'),
            creatorQuoteFee: new BN(poolState.creatorQuoteFee?.toString() || '0'),
            protocolBaseFee: new BN(poolState.protocolBaseFee?.toString() || '0'),
            protocolQuoteFee: new BN(poolState.protocolQuoteFee?.toString() || '0'),
            isMigrated: poolState.isMigrated === 1 || poolState.isMigrated === true,
            migrationProgress: poolState.migrationProgress || 0,
            fetchedAt: new Date(),
            feeClaimerMatchesTreasury,
            expectedFeeClaimer: MEMELAUNCH_TREASURY_PUBKEY.toBase58(),
            actualFeeClaimer,
          };

          results.set(addr, metrics);
        } catch (err) {
          DEBUG && console.warn('[FeeMetricsClient] Failed to decode pool:', addr, err);
          results.set(addr, null);
        }
      }

      DEBUG && console.log('[FeeMetricsClient] Fast batch fetched', results.size, 'pools');
      return results;
    } catch (error) {
      console.error('[FeeMetricsClient] Error in fast batch fetch:', error);
      // Fallback: return empty results rather than throw
      poolAddresses.forEach(addr => results.set(addr.toBase58(), null));
      return results;
    }
  }

  /**
   * Build a claim partner trading fee transaction
   */
  async buildClaimPartnerTradingFeeTransaction(
    poolAddress: PublicKey,
    feeClaimer: PublicKey,
    payer: PublicKey,
    maxBaseAmount: BN,
    maxQuoteAmount: BN
  ): Promise<Transaction> {
    DEBUG && console.log('[FeeMetricsClient] Building claim partner fee tx:', {
      pool: poolAddress.toBase58(),
      feeClaimer: feeClaimer.toBase58(),
      payer: payer.toBase58(),
      maxBaseAmount: maxBaseAmount.toString(),
      maxQuoteAmount: maxQuoteAmount.toString(),
    });

    const tx = await this.dbcClient.partner.claimPartnerTradingFee({
      feeClaimer,
      payer,
      pool: poolAddress,
      maxBaseAmount,
      maxQuoteAmount,
    });

    return tx;
  }

  /**
   * Build a claim creator trading fee transaction.
   * Optionally routes a percentage of claimed SOL to a Market Maker wallet.
   */
  async buildClaimCreatorTradingFeeTransaction(
    poolAddress: PublicKey,
    creator: PublicKey,
    payer: PublicKey,
    maxBaseAmount: BN,
    maxQuoteAmount: BN,
    mmRouting?: { mmWalletPubkey: PublicKey; feeRoutingPercentage: number },
  ): Promise<Transaction> {
    DEBUG && console.log('[FeeMetricsClient] Building claim creator fee tx:', {
      pool: poolAddress.toBase58(),
      creator: creator.toBase58(),
      payer: payer.toBase58(),
      maxBaseAmount: maxBaseAmount.toString(),
      maxQuoteAmount: maxQuoteAmount.toString(),
      mmRouting: mmRouting ? {
        mmWallet: mmRouting.mmWalletPubkey.toBase58(),
        percentage: mmRouting.feeRoutingPercentage,
      } : null,
    });

    const tx = await this.dbcClient.creator.claimCreatorTradingFee({
      creator,
      payer,
      pool: poolAddress,
      maxBaseAmount,
      maxQuoteAmount,
    });

    // Append Market Maker fee routing transfer if configured
    if (mmRouting && mmRouting.feeRoutingPercentage > 0 && mmRouting.feeRoutingPercentage <= 100) {
      const quoteAmountLamports = maxQuoteAmount.toNumber();
      const mmShareLamports = Math.floor(quoteAmountLamports * mmRouting.feeRoutingPercentage / 100);
      if (mmShareLamports > 0) {
        tx.add(
          SystemProgram.transfer({
            fromPubkey: creator,
            toPubkey: mmRouting.mmWalletPubkey,
            lamports: mmShareLamports,
          })
        );
        DEBUG && console.log('[FeeMetricsClient] Added MM fee routing:', {
          mmWallet: mmRouting.mmWalletPubkey.toBase58(),
          lamports: mmShareLamports,
          percentage: mmRouting.feeRoutingPercentage,
        });
      }
    }

    return tx;
  }

  /**
   * Build a claimPositionFee transaction for a DAMM V2 locked position.
   * This is the correct claim mechanism for post-graduation creator fees.
   *
   * Includes:
   * - ATA creation pre-instructions (token A + WSOL if missing)
   * - The claimPositionFee instruction
   * - WSOL unwrap post-instruction (closeAccount converts WSOL ATA to native SOL)
   * - NFT ownership pre-check for clear error messaging
   */
  async buildClaimDammV2PositionFeeTransaction(
    poolAddress: PublicKey,
    positionAddress: PublicKey,
    positionNftMint: PublicKey,
    owner: PublicKey,
    tokenAMint: PublicKey,
    tokenBMint: PublicKey,
    tokenAVault: PublicKey,
    tokenBVault: PublicKey,
    mmRouting?: { mmWalletPubkey: PublicKey; feeRoutingPercentage: number; estimatedQuoteLamports?: number },
  ): Promise<Transaction> {
    DEBUG && console.log('[FeeMetricsClient] Building DAMM V2 claimPositionFee tx:', {
      pool: poolAddress.toBase58(),
      position: positionAddress.toBase58(),
      owner: owner.toBase58(),
    });

    const dammProgram = createDammV2Program(this.connection);

    // Derive ATAs for the owner
    const ownerTokenA = await getAssociatedTokenAddress(tokenAMint, owner);
    const ownerTokenB = await getAssociatedTokenAddress(tokenBMint, owner);
    // DAMM V2 position NFT accounts are PDAs of the DAMM V2 program seeded by
    // [SEED.POSITION_NFT_ACCOUNT, nftMint], NOT standard ATAs. Use the SDK's
    // canonical derivation.
    const positionNftAccount = derivePositionNftAccount(positionNftMint);

    // Verify the position NFT is in the owner's wallet
    try {
      const nftBalance = await this.connection.getTokenAccountBalance(positionNftAccount);
      if (Number(nftBalance.value.amount) === 0) {
        throw new Error('Position NFT not found in wallet. Cannot claim fees.');
      }
    } catch (e) {
      if (e instanceof Error && e.message.includes('Position NFT not found')) throw e;
      throw new Error('Position NFT account does not exist. The position may not belong to this wallet.');
    }

    // Pre-instructions: create ATAs if they don't exist
    const preInstructions: TransactionInstruction[] = [];

    const [ownerTokenAInfo, ownerTokenBInfo] = await this.connection.getMultipleAccountsInfo([
      ownerTokenA,
      ownerTokenB,
    ]);

    if (!ownerTokenAInfo) {
      preInstructions.push(
        createAssociatedTokenAccountIdempotentInstruction(owner, ownerTokenA, owner, tokenAMint)
      );
    }
    if (!ownerTokenBInfo) {
      preInstructions.push(
        createAssociatedTokenAccountIdempotentInstruction(owner, ownerTokenB, owner, tokenBMint)
      );
    }

    // Build the claim instruction
    const claimIx = await dammProgram.methods
      .claimPositionFee()
      .accountsPartial({
        pool: poolAddress,
        position: positionAddress,
        tokenAAccount: ownerTokenA,
        tokenBAccount: ownerTokenB,
        tokenAVault,
        tokenBVault,
        tokenAMint,
        tokenBMint,
        positionNftAccount,
        owner,
        tokenAProgram: TOKEN_PROGRAM_ID,
        tokenBProgram: TOKEN_PROGRAM_ID,
      })
      .instruction();

    // Post-instructions: unwrap WSOL to native SOL
    const postInstructions: TransactionInstruction[] = [];
    if (tokenBMint.equals(NATIVE_MINT)) {
      postInstructions.push(
        createCloseAccountInstruction(ownerTokenB, owner, owner)
      );
    }

    // Compose final transaction
    const tx = new Transaction();
    for (const ix of preInstructions) tx.add(ix);
    tx.add(claimIx);
    for (const ix of postInstructions) tx.add(ix);

    // Append Market Maker fee routing transfer if configured.
    // This goes AFTER the WSOL unwrap (closeAccount) so the SOL is native.
    if (mmRouting && mmRouting.feeRoutingPercentage > 0 && mmRouting.feeRoutingPercentage <= 100 && mmRouting.estimatedQuoteLamports) {
      const mmShareLamports = Math.floor(mmRouting.estimatedQuoteLamports * mmRouting.feeRoutingPercentage / 100);
      if (mmShareLamports > 0) {
        tx.add(
          SystemProgram.transfer({
            fromPubkey: owner,
            toPubkey: mmRouting.mmWalletPubkey,
            lamports: mmShareLamports,
          })
        );
        DEBUG && console.log('[FeeMetricsClient] Added MM fee routing to DAMM V2 claim:', {
          mmWallet: mmRouting.mmWalletPubkey.toBase58(),
          lamports: mmShareLamports,
          percentage: mmRouting.feeRoutingPercentage,
        });
      }
    }

    DEBUG && console.log('[FeeMetricsClient] DAMM V2 claim tx built:', {
      preInstructions: preInstructions.length,
      postInstructions: postInstructions.length,
      totalInstructions: tx.instructions.length,
    });

    return tx;
  }

  /**
   * Get connection for transaction submission
   */
  getConnection(): Connection {
    return this.connection;
  }
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Safely convert lamports BN to SOL string for display.
 * Handles values that exceed JS safe integer limits by using string division.
 */
export function lamportsToSol(lamports: BN): string {
  // Defensive: report unexpected types to Sentry instead of crashing the React tree
  if (typeof lamports === 'number') {
    Sentry.captureMessage('lamportsToSol called with number instead of BN', {
      level: 'warning',
      extra: { value: lamports },
    });
    return ((lamports as unknown as number) / LAMPORTS_PER_SOL).toFixed(6);
  }
  if (!lamports || typeof lamports.lte !== 'function') {
    Sentry.captureMessage('lamportsToSol called with invalid value', {
      level: 'warning',
      extra: { type: typeof lamports, value: String(lamports) },
    });
    return '0.000000';
  }

  // Check if value exceeds safe integer (9_007_199_254_740_991 lamports = ~9,007,199 SOL)
  const MAX_SAFE_LAMPORTS = new BN(Number.MAX_SAFE_INTEGER.toString());

  if (lamports.lte(MAX_SAFE_LAMPORTS)) {
    // Safe to use toNumber
    const lamportsNum = lamports.toNumber();
    const sol = lamportsNum / LAMPORTS_PER_SOL;
    return sol.toFixed(6);
  }
  
  // For very large values, use BN division to get whole SOL
  // and modulo to get fractional part
  const lamportsPerSolBN = new BN(LAMPORTS_PER_SOL.toString());
  const wholeSol = lamports.div(lamportsPerSolBN);
  const remainder = lamports.mod(lamportsPerSolBN);
  
  // Convert remainder to decimal (safe because remainder < 1B)
  const fractional = remainder.toNumber() / LAMPORTS_PER_SOL;
  const fractionalStr = fractional.toFixed(6).slice(2); // Remove "0."
  
  return `${wholeSol.toString()}.${fractionalStr}`;
}

/**
 * Format SOL amount for display
 */
export function formatSolAmount(lamports: BN): string {
  const sol = lamportsToSol(lamports);
  return `${sol} SOL`;
}

/**
 * Formatted creator reward with USD as primary and SOL as secondary
 */
export interface FormattedCreatorReward {
  usdValue: string;  // e.g., "$2.07"
  solValue: string;  // e.g., "0.000046 SOL"
  solNumeric: number; // raw SOL number for calculations
}

/**
 * Format creator reward amount with USD as primary display
 */
export function formatCreatorReward(lamports: BN, solPriceUsd: number | null): FormattedCreatorReward {
  const solString = lamportsToSol(lamports);
  const solNum = parseFloat(solString);
  
  if (solPriceUsd && solPriceUsd > 0) {
    const usd = solNum * solPriceUsd;
    return {
      usdValue: `$${usd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      solValue: `${solString} SOL`,
      solNumeric: solNum,
    };
  }
  
  return {
    usdValue: '—',
    solValue: `${solString} SOL`,
    solNumeric: solNum,
  };
}

/**
 * Check if there are any claimable partner fees
 */
export function hasClaimablePartnerFees(metrics: PoolFeeMetrics): boolean {
  return !metrics.partnerBaseFee.isZero() || !metrics.partnerQuoteFee.isZero();
}

/** Minimum partnerQuoteFee (lamports) for batch claiming. Filters out zero/dust SOL. */
export const MIN_CLAIMABLE_QUOTE_FEE_LAMPORTS = 10_000; // 0.00001 SOL

/** Check if partner fees are worth claiming in batch (has meaningful SOL fees). */
export function isWorthClaimingPartnerFees(
  metrics: PoolFeeMetrics,
  minQuoteLamports: number = MIN_CLAIMABLE_QUOTE_FEE_LAMPORTS,
): boolean {
  return metrics.partnerQuoteFee.gte(new BN(minQuoteLamports));
}

/**
 * Check if there are any claimable creator fees
 */
export function hasClaimableCreatorFees(metrics: PoolFeeMetrics): boolean {
  return !metrics.creatorBaseFee.isZero() || !metrics.creatorQuoteFee.isZero();
}

/**
 * Shorten an address for display
 */
export function shortenAddress(address: string, chars: number = 4): string {
  return `${address.slice(0, chars)}...${address.slice(-chars)}`;
}
